import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { Link } from 'react-router-dom';

function KiWorkshop() {
    const { user, isMember, isAdmin } = useAuth();
    const [invoices, setInvoices] = useState([]);
    const [loadingInvoices, setLoadingInvoices] = useState(false);

    useEffect(() => {
        if (user && (isMember || isAdmin)) {
            fetchInvoices();
        }
    }, [user, isMember, isAdmin]);

    const fetchInvoices = async () => {
        setLoadingInvoices(true);
        try {
            const res = await fetch('/api/member/invoices', {
                headers: { 'Authorization': `Bearer ${user?.token}` }
            });
            const data = await res.json();
            if (data.data) {
                setInvoices(data.data);
            }
        } catch (err) {
            console.error('Fehler beim Laden der Rechnungen:', err);
        } finally {
            setLoadingInvoices(false);
        }
    };

    const downloadPDF = async (invoiceId, invoiceNumber) => {
        try {
            const res = await fetch(`/api/member/invoices/${invoiceId}/pdf`, {
                headers: { 'Authorization': `Bearer ${user?.token}` }
            });
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Rechnung_${invoiceNumber}.pdf`;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            alert('Fehler beim Download: ' + err.message);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('de-DE');
    };

    const formatCurrency = (amount) => {
        return parseFloat(amount || 0).toFixed(2) + ' EUR';
    };

    const getStatusBadge = (status) => {
        if (status === 'paid') return <span className="badge bg-success">Bezahlt</span>;
        return <span className="badge bg-warning">Offen</span>;
    };

    // Nicht eingeloggt
    if (!user) {
        return (
            <div className="text-center py-5">
                <div className="card mx-auto" style={{ maxWidth: '500px' }}>
                    <div className="card-body p-5">
                        <h2 className="mb-4">Ostercamp: KI & Medienkompetenz</h2>
                        <p className="text-muted mb-4">
                            Dieser Bereich ist nur für angemeldete Mitglieder zugänglich.
                        </p>
                        <Link to="/login" className="btn btn-primary">
                            Jetzt anmelden
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    // Eingeloggt aber kein Mitglied
    if (!isMember && !isAdmin) {
        return (
            <div className="text-center py-5">
                <div className="card mx-auto" style={{ maxWidth: '500px' }}>
                    <div className="card-body p-5">
                        <h2 className="mb-4">Zugang nicht möglich</h2>
                        <p className="text-muted">
                            Dieser Bereich ist nur für Mitglieder zugänglich.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="ki-workshop">
            {/* Hero Header */}
            <div className="card mb-4" style={{
                background: 'linear-gradient(135deg, #f810b1, #9b59b6, #324047)',
                color: 'white',
                border: 'none'
            }}>
                <div className="card-body text-center py-5">
                    <p className="mb-2" style={{ opacity: 0.8, fontSize: '1.1rem' }}>Ostercamp 2026</p>
                    <h1 className="mb-3" style={{ color: 'white' }}>KI & Medienkompetenz</h1>
                    <div className="d-flex justify-content-center gap-3 flex-wrap">
                        <span className="badge" style={{ background: 'rgba(255,255,255,0.2)', fontSize: '0.9rem' }}>
                            7-14 Jahre
                        </span>
                        <span className="badge" style={{ background: 'rgba(255,255,255,0.2)', fontSize: '0.9rem' }}>
                            4 Tage
                        </span>
                        <span className="badge" style={{ background: 'rgba(255,255,255,0.2)', fontSize: '0.9rem' }}>
                            09:00 - 13:00 Uhr
                        </span>
                    </div>
                </div>
            </div>

            {/* Wichtige Info zur Altersgruppe */}
            <div className="alert mb-4" style={{ background: '#e3f2fd', borderLeft: '4px solid #2196f3' }}>
                <h5 className="alert-heading" style={{ color: '#1976d2' }}>Gut zu wissen</h5>
                <p className="mb-0">
                    Da wir eine gemischte Altersgruppe haben, passe ich die Aufgaben täglich an – so wird niemand über- oder unterfordert.
                    Ihr bekommt jeden Abend einen kurzen Update, was wir gemacht haben und wie ich den nächsten Tag gestalte.
                </p>
            </div>

            {/* Rechnungen - nur für Mitglieder mit Rechnungen */}
            {invoices.length > 0 && (
                <div className="card mb-4" style={{ borderLeft: '4px solid #00d084' }}>
                    <div className="card-header d-flex justify-content-between align-items-center" style={{ background: 'rgba(0,208,132,0.1)' }}>
                        <h4 className="mb-0" style={{ color: '#00d084' }}>Deine Rechnungen</h4>
                    </div>
                    <div className="card-body">
                        <div className="table-responsive">
                            <table className="table table-hover mb-0">
                                <thead>
                                    <tr>
                                        <th>Rechnungs-Nr.</th>
                                        <th>Kind</th>
                                        <th>Betrag</th>
                                        <th>Status</th>
                                        <th>Aktion</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoices.map(invoice => (
                                        <tr key={invoice.id}>
                                            <td><strong>{invoice.invoice_number}</strong></td>
                                            <td>{invoice.child_name || '-'}</td>
                                            <td>
                                                {formatCurrency(invoice.total)}
                                                {invoice.discount > 0 && (
                                                    <small className="text-success d-block">
                                                        inkl. {invoice.discount_reason || 'Rabatt'}
                                                    </small>
                                                )}
                                            </td>
                                            <td>{getStatusBadge(invoice.status)}</td>
                                            <td>
                                                <button
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={() => downloadPDF(invoice.id, invoice.invoice_number)}
                                                >
                                                    PDF
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="mt-3 p-3 rounded" style={{ background: '#f5f5f0' }}>
                            <strong>Zahlungsinformationen:</strong><br/>
                            Kontoinhaber: Futurhinos<br/>
                            IBAN: DE66 5001 0517 6000 5878 84<br/>
                            BIC: INGDDEFFXXX<br/>
                            <small className="text-muted">Bitte die Rechnungsnummer als Verwendungszweck angeben.</small>
                        </div>
                    </div>
                </div>
            )}

            {/* Wichtige Infos */}
            <div className="card mb-4" style={{ borderLeft: '4px solid #0693e3' }}>
                <div className="card-header" style={{ background: 'rgba(6,147,227,0.1)' }}>
                    <h4 className="mb-0" style={{ color: '#0693e3' }}>Wichtige Infos zum Camp</h4>
                </div>
                <div className="card-body">
                    <div className="row g-4">
                        <div className="col-md-4">
                            <h6><strong>Wann & Wo</strong></h6>
                            <p className="mb-2">
                                <strong>Datum:</strong> 30.03. - 02.04.2026<br/>
                                <strong>Zeit:</strong> 09:00 - 13:00 Uhr
                            </p>
                            <p className="mb-0">
                                <strong>Adresse:</strong><br/>
                                Futurhinos for Kids<br/>
                                Gottlieb-Daimler-Str. 36<br/>
                                73614 Schorndorf
                            </p>
                        </div>
                        <div className="col-md-4">
                            <h6><strong>Was mitbringen?</strong></h6>
                            <ul className="mb-0">
                                <li>Vesper & Getränk für die Pause</li>
                                <li>Geld für Mittagessen (10-15 Euro)</li>
                                <li>Bequeme Kleidung</li>
                                <li>Optional: Buch o.ä. für die Randzeiten</li>
                            </ul>
                            <p className="mt-2 mb-0">
                                <small className="text-muted">
                                    Laptops/Tablets werden von uns gestellt.
                                    Eigene Geräte können gerne mitgebracht werden.
                                </small>
                            </p>
                        </div>
                        <div className="col-md-4">
                            <h6><strong>Kontakt</strong></h6>
                            <p className="mb-2">
                                <strong>Email:</strong><br/>
                                <a href="mailto:info@futurhinos.de">info@futurhinos.de</a>
                            </p>
                            <p className="mb-0">
                                <strong>Telefon:</strong><br/>
                                <a href="tel:+4915129464490">0151 2946 4490</a><br/>
                                <small className="text-muted">(auch WhatsApp)</small>
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Randzeiten Info */}
            <div className="alert mb-4" style={{ background: '#fff3e0', borderLeft: '4px solid #ff9800' }}>
                <h5 className="alert-heading" style={{ color: '#e65100' }}>Randzeiten: 8:00 - 9:00 Uhr & 13:00 - 14:00 Uhr</h5>
                <p className="mb-2">
                    Ihr könnt eure Kinder bereits <strong>ab 8:00 Uhr</strong> bringen und <strong>bis 14:00 Uhr</strong> abholen.
                    Das ist allerdings keine Betreuung im klassischen Sinne - wir sind da und kümmern uns bei Fragen,
                    aber für die Beschäftigung in dieser Zeit sind die Kinder selbst verantwortlich.
                </p>
                <p className="mb-0">
                    <strong>Tipp:</strong> Bringt gerne ein Buch oder ähnliches mit. Wir haben auch Spielsachen da und Rückzugsmöglichkeiten.
                </p>
            </div>

            {/* Mittagessen */}
            <div className="card mb-4" style={{ borderLeft: '4px solid #ff6b35' }}>
                <div className="card-header" style={{ background: 'rgba(255,107,53,0.1)' }}>
                    <h4 className="mb-0" style={{ color: '#ff6b35' }}>Mittagessen</h4>
                </div>
                <div className="card-body">
                    <p className="mb-3">Wir bestellen gemeinsam Mittagessen. Bitte gebt eurem Kind entsprechend Geld mit (ca. 10-15 Euro pro Tag).</p>
                    <div className="row g-3">
                        <div className="col-6 col-md-3">
                            <div className="p-3 rounded text-center" style={{ background: '#f5f5f0' }}>
                                <strong>Montag</strong><br/>
                                <a href="https://www.instagram.com/meatnd_bread/" target="_blank" rel="noreferrer">Meat & Bread</a>
                            </div>
                        </div>
                        <div className="col-6 col-md-3">
                            <div className="p-3 rounded text-center" style={{ background: '#f5f5f0' }}>
                                <strong>Dienstag</strong><br/>
                                <a href="https://www.bergerei-schorndorf.de/" target="_blank" rel="noreferrer">Bergerei</a>
                            </div>
                        </div>
                        <div className="col-6 col-md-3">
                            <div className="p-3 rounded text-center" style={{ background: '#f5f5f0' }}>
                                <strong>Mittwoch</strong><br/>
                                Pizza
                            </div>
                        </div>
                        <div className="col-6 col-md-3">
                            <div className="p-3 rounded text-center" style={{ background: '#f5f5f0' }}>
                                <strong>Donnerstag</strong><br/>
                                <span className="text-muted">wird noch entschieden</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tagesübersicht */}
            <div className="row g-4 mb-4">
                <div className="col-12">
                    <h2 className="mb-3" style={{ color: '#f810b1' }}>Was erwartet euer Kind?</h2>
                </div>

                {/* Tag 1 */}
                <div className="col-md-6 col-lg-3">
                    <div className="card h-100 hover-lift">
                        <div className="card-header text-white" style={{ background: '#f810b1' }}>
                            <strong>Tag 1</strong>
                        </div>
                        <div className="card-body">
                            <h5 className="card-title">Die Grundlagen</h5>
                            <ul className="list-unstyled mb-0">
                                <li className="mb-2">Was ist KI?</li>
                                <li className="mb-2">Fake News & Deepfakes</li>
                                <li className="mb-2">Was ist Prompting?</li>
                                <li>Der Turing-Test</li>
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Tag 2 */}
                <div className="col-md-6 col-lg-3">
                    <a href="/ostercamp/tag-2/" className="text-decoration-none">
                        <div className="card h-100 hover-lift">
                            <div className="card-header text-white" style={{ background: '#9b59b6' }}>
                                <strong>Tag 2</strong>
                            </div>
                            <div className="card-body">
                                <h5 className="card-title" style={{ color: '#324047' }}>Kreativ mit KI</h5>
                                <ul className="list-unstyled mb-0" style={{ color: '#324047' }}>
                                    <li className="mb-2">Meine Traumparty planen</li>
                                    <li className="mb-2">Bilder & Geschichten</li>
                                    <li>Präsentation</li>
                                </ul>
                                <span className="badge mt-2" style={{ background: '#9b59b6' }}>Galerie ansehen</span>
                            </div>
                        </div>
                    </a>
                </div>

                {/* Tag 3 */}
                <div className="col-md-6 col-lg-3">
                    <a href="/ostercamp/tag-3/" className="text-decoration-none">
                        <div className="card h-100 hover-lift">
                            <div className="card-header text-white" style={{ background: '#0693e3' }}>
                                <strong>Tag 3</strong>
                            </div>
                            <div className="card-body">
                                <h5 className="card-title" style={{ color: '#324047' }}>KI & Medienkompetenz</h5>
                                <ul className="list-unstyled mb-0" style={{ color: '#324047' }}>
                                    <li className="mb-2">Wie lernt eine KI?</li>
                                    <li className="mb-2">Was ist ein KI-Agent?</li>
                                    <li>Internetführerschein</li>
                                </ul>
                                <span className="badge mt-2" style={{ background: '#0693e3' }}>Galerie ansehen</span>
                            </div>
                        </div>
                    </a>
                </div>

                {/* Tag 4 */}
                <div className="col-md-6 col-lg-3">
                    <a href="/ostercamp/tag-4/" className="text-decoration-none">
                        <div className="card h-100 hover-lift">
                            <div className="card-header text-white" style={{ background: '#00d084' }}>
                                <strong>Tag 4</strong>
                            </div>
                            <div className="card-body">
                                <h5 className="card-title" style={{ color: '#324047' }}>Abschluss-Projekt</h5>
                                <ul className="list-unstyled mb-0" style={{ color: '#324047' }}>
                                    <li className="mb-2">KI-Buch oder Poster</li>
                                    <li className="mb-2">Präsentation</li>
                                    <li>Zertifikat</li>
                                </ul>
                                <span className="badge mt-2" style={{ background: '#00d084' }}>Galerie ansehen</span>
                            </div>
                        </div>
                    </a>
                </div>
            </div>

            {/* Lernziele */}
            <div className="card mb-4">
                <div className="card-header">
                    <h4 className="mb-0">Lernziele</h4>
                </div>
                <div className="card-body">
                    <div className="row g-4">
                        <div className="col-md-6 col-lg-3">
                            <h6 style={{ color: '#f810b1' }}>KI verstehen</h6>
                            <ul className="small">
                                <li>Wie funktioniert KI?</li>
                                <li>Wie lernt eine KI? (Teachable Machine)</li>
                                <li>Was sind KI-Agenten?</li>
                                <li>Prompts gezielt formulieren</li>
                            </ul>
                        </div>
                        <div className="col-md-6 col-lg-3">
                            <h6 style={{ color: '#0693e3' }}>Kritisches Denken</h6>
                            <ul className="small">
                                <li>Fake News erkennen</li>
                                <li>Deepfakes identifizieren</li>
                                <li>Die 3-Schritte-Prüfung anwenden</li>
                                <li>KI-Ergebnisse hinterfragen</li>
                            </ul>
                        </div>
                        <div className="col-md-6 col-lg-3">
                            <h6 style={{ color: '#00d084' }}>Medienkompetenz</h6>
                            <ul className="small">
                                <li>Sichere Passwörter erstellen</li>
                                <li>Digitaler Fußabdruck verstehen</li>
                                <li>Urheberrecht bei KI-Inhalten</li>
                                <li>Fit für den Internetführerschein</li>
                            </ul>
                        </div>
                        <div className="col-md-6 col-lg-3">
                            <h6 style={{ color: '#9b59b6' }}>Kreativität</h6>
                            <ul className="small">
                                <li>Mit KI Bilder erstellen</li>
                                <li>Eigene Geschichten entwickeln</li>
                                <li>KI-Buch oder Poster gestalten</li>
                                <li>Projekte präsentieren</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tag 1 Details */}
            <div className="card mb-4" style={{ borderLeft: '4px solid #f810b1' }}>
                <div className="card-header" style={{ background: 'rgba(248,16,177,0.1)' }}>
                    <h4 className="mb-0" style={{ color: '#f810b1' }}>Tag 1 im Detail: Die Grundlagen verstehen</h4>
                </div>
                <div className="card-body">
                    <div className="alert mb-4" style={{ background: '#e3f2fd', borderLeft: '4px solid #2196f3' }}>
                        <p className="mb-0">
                            <strong>Philosophie:</strong> Wir starten sanft mit den Grundlagen. Die Kinder sollen erst verstehen,
                            <em>worum es geht</em>, bevor wir in die Praxis einsteigen. So wird niemand überfordert.
                        </p>
                    </div>
                    <div className="row g-3">
                        <div className="col-md-6 col-lg-4">
                            <div className="p-3 rounded" style={{ background: '#f5f5f0' }}>
                                <h6><span className="badge me-2" style={{ background: '#f810b1' }}>09:00</span> Was ist KI?</h6>
                                <p className="small mb-0">
                                    Maus-Film + kindgerechte Erklärung. Anschließend Mensch-vs-KI Fun-Challenges.
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="p-3 rounded" style={{ background: '#f5f5f0' }}>
                                <h6><span className="badge me-2" style={{ background: '#f810b1' }}>10:00</span> Fake News & Deepfakes</h6>
                                <p className="small mb-0">
                                    Was sind Fake News? Kettenbriefe, die 3-Schritte-Prüfung. Danach: WDR KI-Quiz - Echt oder KI?
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="p-3 rounded" style={{ background: '#00d084', color: 'white' }}>
                                <h6><span className="badge bg-white text-dark me-2">11:00</span> Pause</h6>
                                <p className="small mb-0">
                                    Snacks & Erholung - Zeit zum Durchatmen!
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="p-3 rounded" style={{ background: '#f5f5f0' }}>
                                <h6><span className="badge me-2" style={{ background: '#9b59b6' }}>11:30</span> Was ist Prompting?</h6>
                                <p className="small mb-0">
                                    Einfache Erklärung + praktischer Workshop mit Bildgeneratoren.
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="p-3 rounded" style={{ background: '#f5f5f0' }}>
                                <h6><span className="badge me-2" style={{ background: '#0693e3' }}>12:15</span> Der Turing-Test</h6>
                                <p className="small mb-0">
                                    Wer ist Mensch, wer ist KI? Das große Ratespiel zum Abschluss.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tag 2 Details */}
            <div className="card mb-4" style={{ borderLeft: '4px solid #9b59b6' }}>
                <div className="card-header" style={{ background: 'rgba(155,89,182,0.1)' }}>
                    <h4 className="mb-0" style={{ color: '#9b59b6' }}>Tag 2 im Detail: Kreativ mit KI</h4>
                </div>
                <div className="card-body">
                    <div className="alert mb-4" style={{ background: '#f3e5f5', borderLeft: '4px solid #9b59b6' }}>
                        <p className="mb-0">
                            <strong>Projekt:</strong> Jedes Kind plant seine eigene Traumparty - mit selbst erstellten
                            Bildern, einer Einladungskarte und einer kleinen Geschichte. Am Ende präsentiert jeder seine Party!
                        </p>
                    </div>
                    <div className="row g-3">
                        <div className="col-md-6 col-lg-4">
                            <div className="p-3 rounded" style={{ background: '#f5f5f0' }}>
                                <h6><span className="badge me-2" style={{ background: '#9b59b6' }}>09:00</span> Quick, Draw! Wettbewerb</h6>
                                <p className="small mb-0">
                                    Aufwärmen mit dem Zeichen-Ratespiel. Wer schafft es, dass die KI am schnellsten errät?
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="p-3 rounded" style={{ background: '#f5f5f0' }}>
                                <h6><span className="badge me-2" style={{ background: '#9b59b6' }}>09:30</span> Einladungskarte gestalten</h6>
                                <p className="small mb-0">
                                    Mit AutoDraw eine Einladung für die Traumparty erstellen.
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="p-3 rounded" style={{ background: '#f5f5f0' }}>
                                <h6><span className="badge me-2" style={{ background: '#9b59b6' }}>10:00</span> Motto-Bilder erstellen</h6>
                                <p className="small mb-0">
                                    Mit Craiyon Bilder zum Party-Motto generieren (Dino, Weltraum, Unterwasser...).
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="p-3 rounded" style={{ background: '#00d084', color: 'white' }}>
                                <h6><span className="badge bg-white text-dark me-2">10:30</span> Pause</h6>
                                <p className="small mb-0">
                                    Snacks & Erholung
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="p-3 rounded" style={{ background: '#f5f5f0' }}>
                                <h6><span className="badge me-2" style={{ background: '#9b59b6' }}>11:00</span> Geschichte schreiben</h6>
                                <p className="small mb-0">
                                    Mit DuckDuckGo AI eine Geschichte zur Party erfinden. Was passiert auf der Feier?
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="p-3 rounded" style={{ background: '#f5f5f0' }}>
                                <h6><span className="badge me-2" style={{ background: '#9b59b6' }}>11:45</span> Präsentation</h6>
                                <p className="small mb-0">
                                    Jeder stellt seine Traumparty vor. Danach: Freies Arbeiten an eigenen Ideen.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tag 3 Details */}
            <div className="card mb-4" style={{ borderLeft: '4px solid #0693e3' }}>
                <div className="card-header" style={{ background: 'rgba(6,147,227,0.1)' }}>
                    <h4 className="mb-0" style={{ color: '#0693e3' }}>Tag 3 im Detail: KI verstehen & Medienkompetenz</h4>
                </div>
                <div className="card-body">
                    <div className="alert mb-4" style={{ background: '#e3f2fd', borderLeft: '4px solid #0693e3' }}>
                        <p className="mb-0">
                            <strong>Heute:</strong> Wir tauchen tiefer ein - wie lernt eine KI, was ist ein KI-Agent? Außerdem
                            bereiten wir uns auf den <a href="https://futurhinos-kids.de/internetfuehrerschein/" target="_blank" rel="noreferrer">Internetführerschein</a> vor!
                        </p>
                    </div>
                    <div className="row g-3">
                        <div className="col-md-6 col-lg-4">
                            <div className="p-3 rounded" style={{ background: '#f5f5f0' }}>
                                <h6><span className="badge me-2" style={{ background: '#0693e3' }}>09:00</span> Wie lernt eine KI?</h6>
                                <p className="small mb-0">
                                    Mit Teachable Machine eine eigene KI trainieren - live mit der Webcam!
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="p-3 rounded" style={{ background: '#f5f5f0' }}>
                                <h6><span className="badge me-2" style={{ background: '#9b59b6' }}>09:45</span> Was ist ein KI-Agent?</h6>
                                <p className="small mb-0">
                                    Der Unterschied zwischen Chat-KI und KI-Agent. Wie ein Roboter-Assistent!
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="p-3 rounded" style={{ background: '#00d084', color: 'white' }}>
                                <h6><span className="badge bg-white text-dark me-2">10:15</span> Pause</h6>
                                <p className="small mb-0">
                                    Snacks & Erholung
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="p-3 rounded" style={{ background: '#f5f5f0' }}>
                                <h6><span className="badge me-2" style={{ background: '#00d084' }}>10:45</span> Passwörter & Datenschutz</h6>
                                <p className="small mb-0">
                                    Sichere Passwörter erstellen, private Daten schützen.
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="p-3 rounded" style={{ background: '#f5f5f0' }}>
                                <h6><span className="badge me-2" style={{ background: '#ff6b35' }}>11:15</span> Digitaler Fußabdruck & Urheberrecht</h6>
                                <p className="small mb-0">
                                    Was bleibt im Internet? Wem gehören KI-Bilder?
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="p-3 rounded" style={{ background: '#f5f5f0' }}>
                                <h6><span className="badge me-2" style={{ background: '#0693e3' }}>12:15</span> Quiz-Zeit!</h6>
                                <p className="small mb-0">
                                    Interaktives Quiz zur Vorbereitung auf den Internetführerschein.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tag 4 Details */}
            <div className="card mb-4" style={{ borderLeft: '4px solid #00d084' }}>
                <div className="card-header" style={{ background: 'rgba(0,208,132,0.1)' }}>
                    <h4 className="mb-0" style={{ color: '#00d084' }}>Tag 4 im Detail: Abschluss-Projekt</h4>
                </div>
                <div className="card-body">
                    <div className="alert mb-4" style={{ background: '#e8f5e9', borderLeft: '4px solid #00d084' }}>
                        <p className="mb-0">
                            <strong>Projekttag!</strong> Heute erstellen die Kinder ihr eigenes Projekt mit allem was sie gelernt haben.
                            Zur Auswahl stehen: <strong>KI-Buch</strong> (Bildergeschichte) oder <strong>KI-Poster</strong> (Was ich gelernt habe).
                        </p>
                    </div>
                    <div className="row g-3">
                        <div className="col-md-6 col-lg-4">
                            <div className="p-3 rounded" style={{ background: '#f5f5f0' }}>
                                <h6><span className="badge me-2" style={{ background: '#00d084' }}>09:00</span> Projekt-Briefing</h6>
                                <p className="small mb-0">
                                    Welches Projekt? Thema wählen und planen.
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="p-3 rounded" style={{ background: '#f5f5f0' }}>
                                <h6><span className="badge me-2" style={{ background: '#00d084' }}>09:30</span> Bilder erstellen</h6>
                                <p className="small mb-0">
                                    Mit Craiyon, AutoDraw oder eigene Zeichnungen.
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="p-3 rounded" style={{ background: '#00d084', color: 'white' }}>
                                <h6><span className="badge bg-white text-dark me-2">10:15</span> Pause</h6>
                                <p className="small mb-0">
                                    Snacks & Erholung
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="p-3 rounded" style={{ background: '#f5f5f0' }}>
                                <h6><span className="badge me-2" style={{ background: '#00d084' }}>10:45</span> Texte & Layout</h6>
                                <p className="small mb-0">
                                    Geschichte schreiben, Poster gestalten mit Canva.
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="p-3 rounded" style={{ background: '#f5f5f0' }}>
                                <h6><span className="badge me-2" style={{ background: '#f810b1' }}>12:00</span> Präsentationen</h6>
                                <p className="small mb-0">
                                    Jeder stellt sein Projekt vor!
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="p-3 rounded" style={{ background: 'linear-gradient(135deg, #00d084, #0693e3)', color: 'white' }}>
                                <h6><span className="badge bg-white text-dark me-2">12:30</span> Zertifikat</h6>
                                <p className="small mb-0">
                                    Zertifikatsverleihung & Abschluss!
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Übungen für Zuhause */}
            <div className="card mb-4" style={{ borderLeft: '4px solid #f810b1' }}>
                <div className="card-header" style={{ background: 'rgba(248,16,177,0.1)' }}>
                    <h4 className="mb-0" style={{ color: '#f810b1' }}>Übungen für Zuhause</h4>
                </div>
                <div className="card-body">
                    <p className="text-muted mb-4">
                        Diese Übungen können Sie gemeinsam mit Ihrem Kind ausprobieren,
                        um das Gelernte zu vertiefen.
                    </p>

                    <div className="row g-4">
                        {/* Übung 1 */}
                        <div className="col-md-6">
                            <div className="card h-100">
                                <div className="card-body">
                                    <span className="badge bg-success mb-2">Leicht</span>
                                    <h5>Prompt-Challenge</h5>
                                    <p className="small">
                                        Versuchen Sie gemeinsam, mit einem Bildgenerator
                                        (z.B. <a href="https://craiyon.com" target="_blank" rel="noreferrer">Craiyon</a>)
                                        folgendes Bild zu erstellen:
                                    </p>
                                    <div className="p-3 rounded" style={{ background: '#324047', color: 'white', fontFamily: 'monospace' }}>
                                        "Ein Roboter, der Pizza backt, im Stil eines Ölgemäldes"
                                    </div>
                                    <p className="small mt-2 mb-0">
                                        <strong>Variante:</strong> Was passiert, wenn ihr "Cartoon-Stil" oder
                                        "Foto-realistisch" hinzufügt?
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Übung 2 */}
                        <div className="col-md-6">
                            <div className="card h-100">
                                <div className="card-body">
                                    <span className="badge bg-warning mb-2">Mittel</span>
                                    <h5>Fakten-Check</h5>
                                    <p className="small">
                                        Stellt ChatGPT (oder <a href="https://duck.ai" target="_blank" rel="noreferrer">DuckDuckGo AI</a>)
                                        diese Fangfrage:
                                    </p>
                                    <div className="p-3 rounded" style={{ background: '#324047', color: 'white', fontFamily: 'monospace' }}>
                                        "Erzähl mir von dem berühmten Buch 'Der Zeitreisende von München' von Thomas Müller."
                                    </div>
                                    <p className="small mt-2 mb-0">
                                        <strong>Achtung:</strong> Dieses Buch existiert nicht! Erfindet die KI
                                        trotzdem eine Geschichte?
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Übung 3 */}
                        <div className="col-md-6">
                            <div className="card h-100">
                                <div className="card-body">
                                    <span className="badge bg-danger mb-2">Fortgeschritten</span>
                                    <h5>Der 6-Zutaten-Prompt</h5>
                                    <p className="small">
                                        Erstellt gemeinsam einen Bild-Prompt mit allen 6 Zutaten:
                                    </p>
                                    <ul className="small mb-2">
                                        <li><strong>Subjekt:</strong> Was ist im Bild?</li>
                                        <li><strong>Aktion:</strong> Was tut es?</li>
                                        <li><strong>Umgebung:</strong> Wo spielt es?</li>
                                        <li><strong>Stil:</strong> Welcher Kunststil?</li>
                                        <li><strong>Licht:</strong> Welche Beleuchtung?</li>
                                        <li><strong>Kamera:</strong> Welche Perspektive?</li>
                                    </ul>
                                    <p className="small mb-0">
                                        <strong>Ziel:</strong> Je mehr Details, desto besser das Ergebnis!
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Übung 4 */}
                        <div className="col-md-6">
                            <div className="card h-100">
                                <div className="card-body">
                                    <span className="badge bg-info mb-2">Diskussion</span>
                                    <h5>Echt oder Fake?</h5>
                                    <p className="small">
                                        Suchen Sie gemeinsam im Internet nach Bildern und diskutieren Sie:
                                    </p>
                                    <ul className="small mb-2">
                                        <li>Könnte dieses Bild KI-generiert sein?</li>
                                        <li>Worauf achtet man? (Hände, Hintergrund, Details)</li>
                                        <li>Wie kann man es prüfen? (Rückwärts-Bildersuche)</li>
                                    </ul>
                                    <p className="small mb-0">
                                        <strong>Tipp:</strong> Googlen Sie "famous AI generated images 2024"
                                        für Beispiele.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Kostenlose Tools */}
            <div className="card mb-4">
                <div className="card-header">
                    <h4 className="mb-0">Kostenlose KI-Tools zum Ausprobieren</h4>
                </div>
                <div className="card-body">
                    <div className="row g-3">
                        <div className="col-md-6 col-lg-3">
                            <h6 style={{ color: '#f810b1' }}>Zeichen-KI</h6>
                            <ul className="mb-0">
                                <li><a href="https://www.autodraw.com" target="_blank" rel="noreferrer">AutoDraw</a> - KI erkennt deine Skizze</li>
                                <li><a href="https://quickdraw.withgoogle.com" target="_blank" rel="noreferrer">Quick, Draw!</a> - Zeichne, KI errät es</li>
                            </ul>
                        </div>
                        <div className="col-md-6 col-lg-3">
                            <h6 style={{ color: '#f810b1' }}>Bildgeneratoren</h6>
                            <ul className="mb-0">
                                <li><a href="https://craiyon.com" target="_blank" rel="noreferrer">Craiyon</a> - Einfach & kostenlos</li>
                                <li><a href="https://www.bing.com/images/create" target="_blank" rel="noreferrer">Bing Image Creator</a> - Mit Account</li>
                            </ul>
                        </div>
                        <div className="col-md-6 col-lg-3">
                            <h6 style={{ color: '#f810b1' }}>Chat-KI</h6>
                            <ul className="mb-0">
                                <li><a href="https://duck.ai" target="_blank" rel="noreferrer">DuckDuckGo AI</a> - Anonym nutzbar</li>
                                <li><a href="https://copilot.microsoft.com" target="_blank" rel="noreferrer">Microsoft Copilot</a> - Im Browser</li>
                            </ul>
                        </div>
                        <div className="col-md-6 col-lg-3">
                            <h6 style={{ color: '#f810b1' }}>Fake-Erkennung</h6>
                            <ul className="mb-0">
                                <li><a href="https://www1.wdr.de/unternehmen/der-wdr/medienundschule/unterrichtsmaterial/ki-quiz-100.html" target="_blank" rel="noreferrer">WDR KI-Quiz</a> - Echt oder KI?</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            {/* Hinweis für Eltern */}
            <div className="alert" style={{ background: 'rgba(6,147,227,0.15)', color: '#0577b8' }}>
                <h5 className="alert-heading">Hinweis für Eltern</h5>
                <p className="mb-0">
                    Im Workshop legen wir großen Wert auf einen verantwortungsvollen Umgang mit KI.
                    Die Kinder lernen nicht nur, KI-Tools zu nutzen, sondern auch deren Grenzen zu erkennen
                    und Informationen kritisch zu hinterfragen. Bei Fragen zum Workshop können Sie uns
                    jederzeit kontaktieren.
                </p>
            </div>
        </div>
    );
}

export default KiWorkshop;
